
export type ProjectStatus = 'draft' | 'submitted' | 'in_review' | 'approved' | 'published' | 'rejected';

export interface MyAIProject {
  id: string;
  title: string;
  description: string;
  thumbnail: string;
  category: string;
  tools: string[];
  status: ProjectStatus;
  createdAt: string;
  updatedAt: string;
  views: number;
  likes: number;
  comments: number;
  rejectionReason?: string;
  publishedAt?: string;
}

export const statusConfig: Record<ProjectStatus, { label: string; color: string; bgColor: string; icon: string }> = {
  draft: { label: 'Draft', color: 'text-gray-600', bgColor: 'bg-gray-100', icon: 'ri-draft-line' },
  submitted: { label: 'Submitted', color: 'text-sky-600', bgColor: 'bg-sky-50', icon: 'ri-send-plane-line' },
  in_review: { label: 'In Review', color: 'text-amber-600', bgColor: 'bg-amber-50', icon: 'ri-time-line' },
  approved: { label: 'Approved', color: 'text-emerald-600', bgColor: 'bg-emerald-50', icon: 'ri-check-double-line' },
  published: { label: 'Published', color: 'text-teal-600', bgColor: 'bg-teal-50', icon: 'ri-earth-line' },
  rejected: { label: 'Rejected', color: 'text-red-600', bgColor: 'bg-red-50', icon: 'ri-close-circle-line' },
};

export const myAiProjects: MyAIProject[] = [
  {
    id: 'proj-1',
    title: 'Echoes of the Nile',
    description:
      'A sweeping historical drama set along the banks of the Nile, following three generations of a family navigating love, loss, and revolution across a century of Egyptian history.',
    thumbnail:
      'https://public.readdy.ai/ai/img_res/789fc7a7b67a73246d16dec9d50b6911.jpg',
    category: 'Drama',
    tools: ['Sora', 'ElevenLabs', 'Midjourney V6', 'Suno AI'],
    status: 'published',
    createdAt: '2025-01-02T10:30:00Z',
    updatedAt: '2025-01-10T14:20:00Z',
    views: 34200,
    likes: 2890,
    comments: 187,
    publishedAt: '2025-01-10T14:20:00Z',
  },
  {
    id: 'proj-2',
    title: 'Circuit Breaker',
    description:
      'A fast-paced cyberpunk thriller where a rogue hacker discovers a hidden AI network controlling the city infrastructure and must decide whether to expose the truth or join the system.',
    thumbnail:
      'https://readdy.ai/api/search-image?query=Cyberpunk%20hacker%20scene%20in%20dark%20room%20with%20multiple%20holographic%20screens%20neon%20green%20and%20blue%20lighting%20futuristic%20technology%20thriller%20atmosphere%20cinematic%20quality&width=480&height=270&seq=myproj2&orientation=landscape',
    category: 'Thriller',
    tools: ['Runway Gen-4', 'Midjourney V6', 'ElevenLabs'],
    status: 'in_review',
    createdAt: '2025-01-15T08:00:00Z',
    updatedAt: '2025-01-18T11:45:00Z',
    views: 0,
    likes: 0,
    comments: 0,
  },
  {
    id: 'proj-3',
    title: 'The Last Garden',
    description:
      'A meditative short film about an elderly botanist tending the last natural garden on Earth in a world dominated by synthetic ecosystems. A poetic exploration of nature and memory.',
    thumbnail:
      'https://readdy.ai/api/search-image?query=Beautiful%20lush%20garden%20with%20exotic%20flowers%20and%20plants%20elderly%20person%20tending%20garden%20soft%20golden%20morning%20light%20peaceful%20serene%20atmosphere%20cinematic%20quality%20artistic&width=480&height=270&seq=myproj3&orientation=landscape',
    category: 'Drama',
    tools: ['Sora', 'Udio', 'Stable Diffusion XL'],
    status: 'approved',
    createdAt: '2025-01-12T16:00:00Z',
    updatedAt: '2025-01-19T09:30:00Z',
    views: 0,
    likes: 0,
    comments: 0,
  },
  {
    id: 'proj-4',
    title: 'Laugh Protocol',
    description:
      "A comedy series following a malfunctioning AI assistant that develops an obsession with stand-up comedy, disrupting its owner's daily life with poorly timed jokes and punchlines.",
    thumbnail:
      'https://readdy.ai/api/search-image?query=Comedy%20scene%20with%20friendly%20robot%20character%20on%20stage%20performing%20standup%20comedy%20colorful%20spotlight%20warm%20lighting%20audience%20laughing%20entertainment%20venue%20fun%20atmosphere&width=480&height=270&seq=myproj4&orientation=landscape',
    category: 'Comedy',
    tools: ['Pika Labs', 'ChatGPT', 'ElevenLabs'],
    status: 'draft',
    createdAt: '2025-01-20T12:00:00Z',
    updatedAt: '2025-01-20T12:00:00Z',
    views: 0,
    likes: 0,
    comments: 0,
  },
  {
    id: 'proj-5',
    title: 'Starfall Academy',
    description:
      'An anime-inspired action series set in a floating academy where students train to harness cosmic energy. When a dark force threatens the school, a group of unlikely heroes must rise.',
    thumbnail:
      'https://readdy.ai/api/search-image?query=Anime%20style%20floating%20academy%20in%20the%20sky%20with%20cosmic%20energy%20effects%20students%20in%20uniforms%20dramatic%20action%20scene%20vibrant%20colors%20Japanese%20animation%20aesthetic%20epic&width=480&height=270&seq=myproj5&orientation=landscape',
    category: 'Action',
    tools: ['Kling AI', 'Midjourney V6', 'Suno AI', 'ComfyUI'],
    status: 'rejected',
    createdAt: '2024-12-28T09:00:00Z',
    updatedAt: '2025-01-05T16:00:00Z',
    views: 0,
    likes: 0,
    comments: 0,
    rejectionReason:
      'The character designs are too similar to existing copyrighted anime characters. Please revise the character appearances to be more original and resubmit.',
  },
  {
    id: 'proj-6',
    title: 'Desert Mirage',
    description:
      'A visually stunning experimental piece blending real desert photography with AI-generated surreal landscapes, exploring themes of illusion, reality, and the human desire for meaning.',
    thumbnail:
      'https://readdy.ai/api/search-image?query=Surreal%20desert%20landscape%20with%20impossible%20geometric%20structures%20floating%20in%20golden%20sand%20dunes%20mirage%20effect%20dreamlike%20atmosphere%20warm%20orange%20tones%20artistic%20experimental%20cinema&width=480&height=270&seq=myproj6&orientation=landscape',
    category: 'Experimental',
    tools: ['Deforum', 'Stable Diffusion', 'TouchDesigner', 'Udio'],
    status: 'published',
    createdAt: '2024-12-15T14:00:00Z',
    updatedAt: '2024-12-22T10:00:00Z',
    views: 18700,
    likes: 1540,
    comments: 92,
    publishedAt: '2024-12-22T10:00:00Z',
  },
];
